import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { attendanceRegularizationApi } from '../api/attendanceRegularizationApi';
import { appToast } from '../../../../../components/AppToast';
import { appConfirm } from '../../../../../components/AppConfirmModal';

export function useAttendanceRegularizationList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [reasonFilter, setReasonFilter] = useState('All');
  const [datePresetFilter, setDatePresetFilter] = useState('Current Month');
  const [fromDate, setFromDate] = useState('2026-07-01');
  const [toDate, setToDate] = useState('2026-07-31');
  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('Reason');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'All', label: 'All' },
  ];

  const reasonOptions = [
    { value: 'All', label: 'All Reasons' },
    { value: 'Missed In-Punch', label: 'Missed In-Punch' },
    { value: 'Missed Out-Punch', label: 'Missed Out-Punch' },
    { value: 'Work from Home', label: 'Work from Home' },
    { value: 'On Duty / Client Visit', label: 'On Duty / Client Visit' },
    { value: 'Technical Issue', label: 'Technical Issue' },
    { value: 'Others', label: 'Others' },
  ];

  const datePresetOptions = [
    { value: 'Current Month', label: 'Current Month' },
    { value: 'Last Month', label: 'Last Month' },
    { value: 'This Quarter', label: 'This Quarter' },
    { value: 'Custom Range', label: 'Custom Range' },
  ];

  const searchFieldOptions = [
    { value: 'Reason', label: 'Reason' },
    { value: 'Remarks', label: 'Remarks' },
    { value: 'Date', label: 'Date' },
  ];

  const tools = [
    { key: 'excel', label: 'Export to Excel', icon: 'table_view' },
    { key: 'pdf', label: 'Export to PDF', icon: 'picture_as_pdf' },
    { key: 'print', label: 'Print', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await attendanceRegularizationApi.getList();
      setRecords(res.data || []);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle Preset Change
  const handleDatePresetChange = useCallback((preset) => {
    setDatePresetFilter(preset);
    if (preset === 'Current Month') {
      setFromDate('2026-07-01');
      setToDate('2026-07-31');
    } else if (preset === 'Last Month') {
      setFromDate('2026-06-01');
      setToDate('2026-06-30');
    }
  }, []);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Reason filter
      if (reasonFilter !== 'All' && item.reason !== reasonFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Reason' && !item.reason.toLowerCase().includes(query)) return false;
        if (searchField === 'Remarks' && !item.remarks.toLowerCase().includes(query)) return false;
        if (searchField === 'Date' && !item.date.toLowerCase().includes(query) && !item.dateTime.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, statusFilter, reasonFilter, searchText, searchField]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleView = useCallback(
    (record) => {
      navigate(`/attendance/attendance-regularization/entry?id=${record.id}&mode=view`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (record) => {
      navigate(`/attendance/attendance-regularization/entry?id=${record.id}&mode=edit`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (id) => {
      const confirmed = await appConfirm({ title: 'Delete Regularization Request', message: 'Are you sure you want to delete this regularization request?', confirmText: 'Delete', variant: 'danger' });
      if (confirmed) {
        await attendanceRegularizationApi.delete(id);
        appToast.success('Regularization entry deleted successfully');
        loadData();
      }
    },
    [loadData]
  );

  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Filters applied');
  }, []);

  const resetFilters = useCallback(() => {
    setStatusFilter('All');
    setReasonFilter('All');
    setDatePresetFilter('Current Month');
    setFromDate('2026-07-01');
    setToDate('2026-07-31');
    setSearchText('');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('excel')) {
      appToast.success('Exporting Regularization records to Excel...');
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Regularization records to PDF...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      statusFilter,
      statusOptions,
      reasonFilter,
      datePresetFilter,
      fromDate,
      toDate,
      searchText,
      searchField,
      isLoading,
      reasonOptions,
      datePresetOptions,
      searchFieldOptions,
      tools,
    },
    actions: {
      setStatusFilter,
      setReasonFilter,
      setDatePresetFilter: handleDatePresetChange,
      setFromDate,
      setToDate,
      setSearchText,
      setSearchField,
      setPage,
      setPageSize,
      handleView,
      handleEdit,
      handleDelete,
      handleSearch,
      resetFilters,
      handleToolSelect,
      refresh: loadData,
    },
  };
}
